import clsx from 'clsx';
import React from 'react';

import Comments from '@components/Comments';
import FadeWrapper from '@components/FadeWrapper';
import MostPopularArticles from '@components/MostPopularArticles';
import Paywall from '@components/Paywall';
import RelatedStories from '@components/RelatedStories';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';
import { LeadTypes } from '@utils/enums';

import { BriefingProps } from '.';
import Body from './Body';
import BriefingDetails from './BriefingDetails';
import ContinuedReadingContainer from './ContinuedReadingContainer';
import Headline from './Headline';
import LockedBriefing from './LockedBriefing';

const BriefingContent = ({
  briefing,
  canViewFullBriefing,
  currentUserPresent,
  hideAwarenessBar,
  isSEOBot,
  mostPopularArticles,
  paywallProps,
  relatedArticles,
  teaser,
}: BriefingProps) => {
  const { authors, body, publishedAt, commentsCount, headline, id, relatedItems, source } =
    briefing;
  const hydrated = useServerSideRendering();
  const shouldShowPaywall = !canViewFullBriefing && !isSEOBot;
  const { Track } = useEventTracking({
    contentId: id,
    contentType: ContentType.Briefing,
    page: Page.Briefing,
    pageId: id,
  });

  const shouldShowFullBleedPaywall = shouldShowPaywall && !currentUserPresent;
  const shouldShowStandardPaywall = shouldShowPaywall && currentUserPresent;

  return (
    <Track>
      <div
        className={clsx('relative pb-64 sm:pb-112', {
          'pt-32 sm:pt-48': canViewFullBriefing,
          'pt-90 sm:pt-[140px]': !canViewFullBriefing && hideAwarenessBar,
          'pt-[155px] sm:pt-[140px]': !canViewFullBriefing && !hideAwarenessBar,
        })}
      >
        {shouldShowFullBleedPaywall ? (
          <LockedBriefing briefing={briefing} paywallProps={paywallProps} />
        ) : (
          <>
            <div className="px-16 sm:px-28">
              <article className="m-auto lg:max-w-[1250px] xl:max-w-container-lg">
                <Headline headline={headline} />
                <div className="block pb-48 pt-32 sm:pt-48 md:hidden">
                  <BriefingDetails
                    authors={authors}
                    publishedAt={publishedAt}
                    source={source}
                    canViewFullBriefing={canViewFullBriefing}
                  />
                </div>
                <div
                  className={clsx('flex pt-0 md:gap-72 md:pt-64 xl:gap-112', {
                    'pb-64 ': !canViewFullBriefing,
                  })}
                >
                  <div className="hidden flex-shrink-0 md:block md:w-[195px] lg:w-[260px]">
                    <div className="flex flex-col">
                      <BriefingDetails
                        authors={authors}
                        publishedAt={publishedAt}
                        source={source}
                        canViewFullBriefing={canViewFullBriefing}
                      />
                      {canViewFullBriefing && (
                        <MostPopularArticles
                          mostPopularArticles={mostPopularArticles}
                          className="md:mt-80 xl:mt-[102px] xl:max-w-[230px]"
                        />
                      )}
                    </div>
                  </div>
                  <div className="relative flex-1">
                    {isSEOBot || canViewFullBriefing ? (
                      <Body body={body} isSEOBot={isSEOBot} />
                    ) : (
                      <FadeWrapper>{teaser}</FadeWrapper>
                    )}
                    {canViewFullBriefing && (
                      <>
                        {relatedItems.length > 0 && (
                          <ContinuedReadingContainer relatedItems={relatedItems} />
                        )}
                        {hydrated && (
                          <div className="mt-80 sm:mt-112">
                            <Comments
                              commentableId={id}
                              commentableType={CommentableType.Briefing}
                              commentsCount={commentsCount}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </article>
              <div className="m-auto lg:max-w-[1250px] xl:max-w-container-lg">
                <RelatedStories articles={relatedArticles} className="mt-80 sm:mt-112" />
              </div>
            </div>
            {shouldShowStandardPaywall && hydrated && (
              <div className="sticky -bottom-px z-50 mt-0 w-full bg-white sm:bg-grayscale-charcoal">
                <Paywall leadType={LeadTypes.BriefingLead} {...paywallProps} />
              </div>
            )}
          </>
        )}
      </div>
    </Track>
  );
};

export default BriefingContent;
